export interface InteractiveImageExample {
  /** The edit prompt that types out in the bottom pill. */
  prompt: string;
  /** Public path (or remote URL) of the image shown for this example. */
  imageUrl: string;
  /** Optional blur placeholder for next/image. */
  blurDataURL?: string;
  /** Optional alt text; falls back to the prompt. */
  alt?: string;
  /**
   * Groups consecutive examples into one subject. Same-scenario moves crossfade
   * (edit); cross-scenario moves translate the scenario strip (new generation).
   * Untagged examples all crossfade.
   */
  scenario?: string;
}

/**
 * A result revealed in "create your own" mode. Without an injected `onGenerate`
 * the demo cycles a canned set after a fixed delay (prompt ignored); with it,
 * each result is a real generation appended to the set.
 */
export interface InteractiveImageCreationResult {
  /** Public path (or remote URL) of the generated image. */
  imageUrl: string;
  /** Optional blur placeholder for next/image. */
  blurDataURL?: string;
  /** Optional alt text. */
  alt?: string;
  /**
   * Opaque id letting the producer chain edits onto this result. The component
   * never interprets it, only passes it back via
   * {@link InteractiveImageGenerationInput.baseImageAssetId}, keeping `_ui`
   * backend-agnostic.
   */
  assetId?: string;
  /**
   * Producer-built destination for the post-generation CTA (e.g. a deep link that
   * claims this asset). Rendered on the result's "sign up to download" CTA; the demo
   * falls back to `signUpHref` when absent (the canned, no-backend path).
   */
  claimUrl?: string;
}

/**
 * Input handed to an injected {@link InteractiveImageGenerate}. The component
 * gathers prompt, attachments, the prior result's id (when editing), and an
 * abort signal, then delegates the generation to the wrapper.
 */
export interface InteractiveImageGenerationInput {
  /** The user's prompt (trimmed; may be empty when only an attachment is supplied). */
  prompt: string;
  /** Raw picked reference files (capped at one — the source image for a first generation). */
  attachments: File[];
  /** Opaque id of the result being edited, chained as the edit source (no re-upload). */
  baseImageAssetId?: string;
  /** Aborted by stop / re-engage / leaving create mode / unmount. */
  signal: AbortSignal;
}

/** Injected generator: turns an input into a result. Rejects to signal a failure. */
export type InteractiveImageGenerate = (
  input: InteractiveImageGenerationInput,
) => Promise<InteractiveImageCreationResult>;

/**
 * A generation failure, mapped by the producer into one of two card treatments: `gated`
 * (quota / rate-limit) shows the sign-up CTA; `retry` shows an inline "Try again".
 */
export interface InteractiveImageGenerationError {
  kind: "gated" | "retry";
  message: string;
  /**
   * Producer-built destination for the `gated` CTA (e.g. a deep link prefilled with the prompt
   * and input asset). Rendered on the gated sign-up CTA; falls back to `signUpHref` when absent.
   */
  ctaUrl?: string;
}

/** Timing for the auto-play sequence (typing → reveal → hold → next). */
export const TIMING = {
  /** Delay between each word appearing while the prompt types out. */
  WORD_INTERVAL_MS: 110,
  /** Pause after the prompt finishes typing, before the result crossfades in. */
  HOLD_BEFORE_RESULT_MS: 0,
  /** How long the revealed result is held before advancing to the next example. */
  HOLD_AFTER_RESULT_MS: 2200,
  /** Image crossfade duration, in seconds (motion transition). */
  CROSSFADE_S: 0.6,
  /** Fake "generating…" delay in create mode before a canned result reveals, in ms. */
  GENERATE_MS: 3000,
} as const;

/**
 * Layout-motion transition for switching between scenarios: scenario cards sit on one strip at
 * 100% intervals. Clicks update a target position; the rendered position continuously aims at it.
 */
export const SCENARIO_TRANSITION = {
  /** Spring pull toward the target scenario; higher catches up faster. */
  SPRING_STIFFNESS: 48,
  /** Spring damping; keeps the target chase smooth without restarting on each click. */
  SPRING_DAMPING: 12.5,
  /** Fade-in for the spring pull after leaving rest, in ms. Later clicks while moving do not reset it. */
  SOFT_START_MS: 320,
  /** Scale reached while the strip still has meaningful distance left to travel. */
  MOVING_SCALE: 0.675,
  /** Remaining slot distance where the card starts growing back to full size. */
  SCALE_DISTANCE: 0.23,
  /** Fast shrink/grow; blended continuously from the current scale delta. */
  SCALE_DOWN_STIFFNESS: 96,
  SCALE_UP_STIFFNESS: 34,
  SCALE_RESPONSE_RANGE: 0.08,
  SCALE_DAMPING: 12,
} as const;

/** Shared elevated-pill shadow (matches the Figma "Sm" effect, as used by PromptIsland). */
export const PILL_SHADOW =
  "tw-shadow-[0_0_0.0625rem_0_rgba(0,0,0,0.4),0_0.0625rem_0.125rem_0_rgba(0,0,0,0.04),0_0.125rem_0.125rem_0_rgba(0,0,0,0.04)]";

/** Standard ease curve (Material/Figma "standard") shared across the demo's motion transitions. */
export const EASE_STANDARD = [0.4, 0, 0.2, 1] as const;

/** Opacity-crossfade transition; collapses to an instant cut under reduced motion. */
export const makeCrossfade = (reduceMotion: boolean) => ({
  duration: reduceMotion ? 0 : TIMING.CROSSFADE_S,
  ease: EASE_STANDARD,
});
